<template>
  <div class="charts-container">
    <a-row :gutter="16">
      <a-col :span="12">
        <a-card title="火山警戒级别分布">
          <v-chart class="chart" :option="alertLevelOption" autoresize />
        </a-card>
      </a-col>
      <a-col :span="12">
        <a-card title="航空代码分布">
          <v-chart class="chart" :option="aviationCodeOption" autoresize />
        </a-card>
      </a-col>
    </a-row>
    <a-row :gutter="16" style="margin-top: 16px">
      <a-col :span="24">
        <a-card title="火山活动趋势">
          <v-chart class="chart" :option="activityTrendOption" autoresize />
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { PieChart, LineChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';

use([
  CanvasRenderer,
  PieChart,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
]);

export default defineComponent({
  name: 'VolcanoCharts',
  components: {
    VChart,
  },
  props: {
    volcanoes: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const alertLevelOption = ref({});
    const aviationCodeOption = ref({});
    const activityTrendOption = ref({});
    const warningCounts = ref<number[]>([]);
    const watchCounts = ref<number[]>([]);
    const advisoryCounts = ref<number[]>([]);
    const dates = ref<string[]>([]);

    const updateCharts = () => {
      // 警戒级别分布
      const alertLevelStats = {
        NORMAL: 0,
        ADVISORY: 0,
        WATCH: 0,
        WARNING: 0,
      };

      // 航空代码分布
      const aviationCodeStats = {
        GREEN: 0,
        YELLOW: 0,
        ORANGE: 0,
        RED: 0,
      };

      props.volcanoes.forEach((volcano: any) => {
        const status = volcano.status;
        if (status) {
          alertLevelStats[status.alertLevel] = (alertLevelStats[status.alertLevel] || 0) + 1;
          aviationCodeStats[status.aviationColorCode] = (aviationCodeStats[status.aviationColorCode] || 0) + 1;
        }
      });

      // 更新警戒级别饼图
      alertLevelOption.value = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)',
        },
        legend: {
          orient: 'vertical',
          left: 'left',
        },
        series: [
          {
            type: 'pie',
            radius: '70%',
            data: Object.entries(alertLevelStats).map(([name, value]) => ({
              name,
              value,
              itemStyle: {
                color: getAlertLevelColor(name),
              },
            })),
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)',
              },
            },
          },
        ],
      };

      // 更新航空代码饼图
      aviationCodeOption.value = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)',
        },
        legend: {
          orient: 'vertical',
          left: 'left',
        },
        series: [
          {
            type: 'pie',
            radius: '70%',
            data: Object.entries(aviationCodeStats).map(([name, value]) => ({
              name,
              value,
              itemStyle: {
                color: getAviationCodeColor(name),
              },
            })),
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)',
              },
            },
          },
        ],
      };

      // 更新活动趋势折线图
      generateRandomData();

      activityTrendOption.value = {
        tooltip: {
          trigger: 'axis',
        },
        legend: {
          data: ['WARNING', 'WATCH', 'ADVISORY'],
        },
        xAxis: {
          type: 'category',
          data: dates.value,
        },
        yAxis: {
          type: 'value',
        },
        series: [
          {
            name: 'WARNING',
            type: 'line',
            data: warningCounts.value,
            itemStyle: { color: '#ff4d4f' },
          },
          {
            name: 'WATCH',
            type: 'line',
            data: watchCounts.value,
            itemStyle: { color: '#faad14' },
          },
          {
            name: 'ADVISORY',
            type: 'line',
            data: advisoryCounts.value,
            itemStyle: { color: '#1890ff' },
          },
        ],
      };
    };

    const getAlertLevelColor = (level: string) => {
      const colors: Record<string, string> = {
        NORMAL: '#52c41a',
        ADVISORY: '#1890ff',
        WATCH: '#faad14',
        WARNING: '#ff4d4f',
      };
      return colors[level] || '#d9d9d9';
    };

    const getAviationCodeColor = (code: string) => {
      const colors: Record<string, string> = {
        GREEN: '#52c41a',
        YELLOW: '#faad14',
        ORANGE: '#fa8c16',
        RED: '#ff4d4f',
      };
      return colors[code] || '#d9d9d9';
    };

    const generateRandomData = () => {
      // 清空原有数据
      warningCounts.value = [];
      watchCounts.value = [];
      advisoryCounts.value = [];
      dates.value = [];

      // 生成过去7天的数据
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        dates.value.push(date.toLocaleDateString());
        warningCounts.value.push(Math.floor(Math.random() * 3));
        watchCounts.value.push(Math.floor(Math.random() * 5));
        advisoryCounts.value.push(Math.floor(Math.random() * 8));
      }
    };

    watch(() => props.volcanoes, updateCharts, { deep: true });

    return {
      alertLevelOption,
      aviationCodeOption,
      activityTrendOption,
      warningCounts,
      watchCounts,
      advisoryCounts,
      dates,
    };
  },
});
</script>

<style scoped>
.charts-container {
  margin: 20px 0;
}
.chart {
  height: 300px;
}
</style>
